// Typography rules (see src/design-system/TYPOGRAPHY_AND_THEME.md):
//   - Every title uses a typography.h* preset, OR `font-heading font-bold` directly.
//   - Body text inherits --font-body automatically; do not set fontFamily.
//   - Brand wordmark (myvitae.ai) keeps Quicksand via logoFontStyle — don't touch.
import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  DangerButton,
  Modal,
  SecondaryButton,
  XIcon,
} from "../../../design-system";
import { borders, surfaces, textColors, typography } from "../../../design-system/tokens";
import { useAdminMessage } from "../../hooks/useAdminMessages";
import { formatCount, formatDateTime, formatRelativeTime } from "../automations/automationUtils";
import ChannelChip, { TemplateChip } from "./ChannelChip";
import MessageStatusBadge from "./MessageStatusBadge";
import OriginLabel from "./OriginLabel";
import RecipientKindBadge, { PaidChip } from "./RecipientKindBadge";
import { describeRecipient, isCancellable } from "./messagesUtils";

const BODY_VIEWS = [
  { value: "rendered", label: "Rendered email" },
  { value: "markdown", label: "Markdown" },
];

// ---------------------------------------------------------------------------
// Small presentational bits
// ---------------------------------------------------------------------------

const Fact = ({ label, children, span = false }) => (
  <div className={`min-w-0 ${span ? "sm:col-span-2" : ""}`}>
    <dt className={`${typography.caption} ${textColors.muted} uppercase tracking-wider`}>{label}</dt>
    <dd className={`mt-0.5 ${typography.bodySmall} ${textColors.primary} break-words`}>{children}</dd>
  </div>
);

Fact.propTypes = { label: PropTypes.string.isRequired, children: PropTypes.node, span: PropTypes.bool };

const Dash = () => <span className={textColors.muted}>—</span>;

const DateFact = ({ value, hint }) => {
  if (!value) return <Dash />;
  return (
    <>
      <span title={value}>{formatDateTime(value)}</span>
      <span className={`block ${typography.caption} ${textColors.tertiary}`}>
        {formatRelativeTime(value)}
        {hint ? ` · ${hint}` : ""}
      </span>
    </>
  );
};

DateFact.propTypes = { value: PropTypes.string, hint: PropTypes.string };

const MarkdownBlock = ({ body }) =>
  body ? (
    <pre
      className={`whitespace-pre-wrap break-words rounded-xl border ${borders.default} ${surfaces.secondary} ${textColors.secondary} p-4 text-sm leading-relaxed max-h-[600px] overflow-y-auto`}
    >
      {body}
    </pre>
  ) : (
    <p className={`${typography.bodySmall} ${textColors.muted}`}>Empty body.</p>
  );

MarkdownBlock.propTypes = { body: PropTypes.string };

const DetailSkeleton = () => (
  <div className="animate-pulse space-y-4" aria-busy="true" aria-live="polite">
    <div className={`h-6 ${surfaces.tertiary} rounded w-2/3`} />
    <div className="flex gap-2">
      <div className={`h-5 ${surfaces.tertiary} rounded-full w-20`} />
      <div className={`h-5 ${surfaces.tertiary} rounded-full w-16`} />
      <div className={`h-5 ${surfaces.tertiary} rounded-full w-24`} />
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="space-y-2">
          <div className={`h-3 ${surfaces.tertiary} rounded w-1/3`} />
          <div className={`h-4 ${surfaces.tertiary} rounded w-3/4`} />
        </div>
      ))}
    </div>
    <div className={`h-48 ${surfaces.tertiary} rounded-xl`} />
    <span className="sr-only">Loading message…</span>
  </div>
);

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------

/**
 * Full detail of one outbound message: header (title + status / channel /
 * template chips), a facts grid (recipient, origin, timing, sender, attempts,
 * dedup key, error), the body — rendered branded email in a sandboxed iframe
 * or the markdown source — and the raw metadata. Scheduled rows can be
 * cancelled from the footer (the page owns the confirmation + toast).
 */
const MessageDetailModal = ({
  isOpen,
  messageId,
  onClose,
  onCancel,
  cancelPending = false,
  onAutomationSelect,
}) => {
  const detailQuery = useAdminMessage(messageId, { enabled: isOpen && messageId != null });
  const message = detailQuery.data?.message || null;

  const [bodyView, setBodyView] = useState("rendered");
  const [metadataOpen, setMetadataOpen] = useState(false);
  useEffect(() => {
    setBodyView("rendered");
    setMetadataOpen(false);
  }, [messageId]);

  const recipient = useMemo(() => describeRecipient(message), [message]);
  const metadataEntries = useMemo(
    () => (message?.metadata && typeof message.metadata === "object" ? Object.keys(message.metadata) : []),
    [message]
  );
  const metadataJson = useMemo(() => {
    try {
      return JSON.stringify(message?.metadata ?? {}, null, 2);
    } catch {
      return String(message?.metadata ?? "");
    }
  }, [message]);

  const isEmail = message?.channel === "email";
  const renderedHtml = isEmail ? message?.rendered_html || "" : "";
  const effectiveBodyView = isEmail && renderedHtml && bodyView === "rendered" ? "rendered" : "markdown";
  const canCancel = isCancellable(message);

  const footer = (
    <>
      {canCancel && onCancel ? (
        <DangerButton
          variant="outline"
          size="sm"
          leftIcon={<XIcon size="sm" />}
          onClick={() => onCancel(message)}
          loading={cancelPending}
          title="Cancel this scheduled message — it will not be sent"
        >
          Cancel message
        </DangerButton>
      ) : null}
      <SecondaryButton size="sm" onClick={onClose}>
        Close
      </SecondaryButton>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={messageId != null ? `Message #${messageId}` : "Message"}
      size="xl"
      className="mx-4 lg:max-w-5xl"
      bodyMaxHeight="calc(100vh - 200px)"
      footer={footer}
    >
      {detailQuery.isLoading || (!message && detailQuery.isFetching) ? (
        <DetailSkeleton />
      ) : detailQuery.error ? (
        <div className="rounded-2xl px-4 py-3 bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800" role="alert">
          <p className={`${typography.bodySmall} text-red-800 dark:text-red-300`}>
            Failed to load the message: {detailQuery.error?.message || "Unknown error"}
          </p>
          <div className="mt-2">
            <SecondaryButton size="sm" onClick={() => detailQuery.refetch()} disabled={detailQuery.isFetching}>
              Retry
            </SecondaryButton>
          </div>
        </div>
      ) : !message ? (
        <p className={`${typography.bodySmall} ${textColors.muted}`}>No message selected.</p>
      ) : (
        <div className="space-y-6">
          {/* Header */}
          <header className="space-y-2">
            <h3 className={`${typography.h4} ${textColors.primary} break-words`}>{message.title || "(untitled)"}</h3>
            <div className="flex flex-wrap items-center gap-2">
              <MessageStatusBadge status={message.status} />
              <ChannelChip channel={message.channel} />
              <TemplateChip channel={message.channel} variant={message.email_template_variant} />
              {message.sender_display_name ? (
                <span className={`${typography.caption} ${textColors.tertiary}`}>From {message.sender_display_name}</span>
              ) : null}
            </div>
            {message.status === "scheduled" ? (
              <p className={`${typography.caption} ${textColors.tertiary}`}>
                Scheduled rows are sent no earlier than their scheduled time, by the next delivery cycle.
              </p>
            ) : null}
          </header>

          {/* Facts */}
          <section aria-labelledby="message-facts-title" className={`rounded-2xl border ${borders.glass} ${surfaces.glass} p-4`}>
            <h4 id="message-facts-title" className={`${typography.h5} ${textColors.primary} mb-3`}>
              Details
            </h4>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
              <Fact label="Recipient">
                {recipient.name ? <span className="font-medium">{recipient.name}</span> : null}
                {recipient.email ? (
                  <span className={`block ${typography.caption} ${textColors.tertiary} break-all`}>{recipient.email}</span>
                ) : null}
                {!recipient.name && !recipient.email ? <Dash /> : null}
                <span className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  <RecipientKindBadge kind={message.recipient_kind} />
                  {message.recipient_kind === "user" ? <PaidChip value={message.recipient_was_paid} /> : null}
                  {message.user?.id != null ? (
                    <span className={`${typography.caption} ${textColors.muted}`}>user #{message.user.id}</span>
                  ) : null}
                </span>
              </Fact>
              <Fact label="Origin">
                <OriginLabel message={message} onAutomationSelect={onAutomationSelect} />
                {message.origin === "automation" ? (
                  <span className={`block mt-1 ${typography.caption} ${textColors.tertiary}`}>
                    {message.automation_run_id != null ? `Run #${message.automation_run_id}` : "No run recorded"}
                    {message.action_index != null ? ` · action #${Number(message.action_index) + 1}` : ""}
                  </span>
                ) : null}
              </Fact>
              <Fact label="Scheduled for">
                <DateFact value={message.scheduled_for} hint={message.status === "scheduled" ? "no earlier than" : ""} />
              </Fact>
              <Fact label="Sent at">
                <DateFact value={message.sent_at} />
              </Fact>
              <Fact label="Created">
                <DateFact value={message.created_at} />
              </Fact>
              <Fact label="Last updated">
                <DateFact value={message.updated_at} />
              </Fact>
              <Fact label="Sender">{message.sender_display_name || <Dash />}</Fact>
              <Fact label="Attempts">
                <span className="tabular-nums">{formatCount(message.attempts || 0)}</span>
              </Fact>
              <Fact label="Dedup key" span>
                {message.dedup_key ? <code className="font-mono text-xs break-all">{message.dedup_key}</code> : <Dash />}
              </Fact>
              {message.error ? (
                <Fact label="Error" span>
                  <div
                    className="rounded-xl px-3 py-2 bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 text-sm break-words whitespace-pre-wrap"
                    role="note"
                  >
                    {message.error}
                  </div>
                </Fact>
              ) : null}
            </dl>
          </section>

          {/* Body */}
          <section aria-labelledby="message-body-title" className="space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <h4 id="message-body-title" className={`${typography.h5} ${textColors.primary}`}>
                {isEmail ? "Email" : "Inbox message"}
              </h4>
              {isEmail ? (
                <div role="group" aria-label="Body view" className={`inline-flex rounded-full border ${borders.default} ${surfaces.tertiary} p-0.5`}>
                  {BODY_VIEWS.map((view) => {
                    const active = effectiveBodyView === view.value;
                    const disabled = view.value === "rendered" && !renderedHtml;
                    return (
                      <button
                        key={view.value}
                        type="button"
                        aria-pressed={active}
                        disabled={disabled}
                        onClick={() => setBodyView(view.value)}
                        title={disabled ? "Rendered preview unavailable for this message" : undefined}
                        className={`${typography.button} px-3 py-1 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 disabled:opacity-50 disabled:cursor-not-allowed ${
                          active ? "bg-blue-600 dark:bg-blue-500 text-white" : `${textColors.secondary} ${surfaces.hover}`
                        }`}
                      >
                        {view.label}
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>

            {isEmail && !renderedHtml ? (
              <div className="rounded-2xl px-4 py-3 bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800">
                <p className={`${typography.bodySmall} text-yellow-800 dark:text-yellow-300`}>
                  The rendered preview is unavailable for this message (the template could not be rendered) — showing the
                  markdown source instead.
                </p>
              </div>
            ) : null}

            {effectiveBodyView === "rendered" ? (
              <>
                <p className={`${typography.caption} ${textColors.tertiary}`}>
                  The branded template as the recipient saw it (links are inert in this preview).
                </p>
                {/* Intentionally light in both themes: this is the recipient's email, not our UI. */}
                <iframe
                  title="Rendered email preview"
                  sandbox=""
                  srcDoc={renderedHtml}
                  className={`w-full h-[600px] rounded-xl border ${borders.default} bg-white dark:bg-white`}
                />
              </>
            ) : (
              <>
                {!isEmail ? (
                  <p className={`${typography.caption} ${textColors.tertiary}`}>Markdown source of the inbox message.</p>
                ) : null}
                <MarkdownBlock body={message.body_markdown} />
              </>
            )}
          </section>

          {/* Metadata */}
          <section aria-labelledby="message-metadata-title" className={`rounded-2xl border ${borders.glass} ${surfaces.glass} p-4`}>
            <div className="flex items-center justify-between gap-3">
              <h4 id="message-metadata-title" className={`${typography.h5} ${textColors.primary}`}>
                Metadata
                <span className={`ml-2 ${typography.caption} ${textColors.muted} font-normal`}>
                  {metadataEntries.length === 0 ? "none recorded" : `${formatCount(metadataEntries.length)} key${metadataEntries.length === 1 ? "" : "s"}`}
                </span>
              </h4>
              {metadataEntries.length > 0 ? (
                <SecondaryButton
                  size="sm"
                  onClick={() => setMetadataOpen((v) => !v)}
                  aria-expanded={metadataOpen}
                  aria-controls="message-metadata-panel"
                  rightIcon={metadataOpen ? <ChevronUpIcon size="sm" /> : <ChevronDownIcon size="sm" />}
                >
                  {metadataOpen ? "Hide" : "Show"}
                </SecondaryButton>
              ) : null}
            </div>
            {metadataOpen && metadataEntries.length > 0 ? (
              <pre
                id="message-metadata-panel"
                className={`mt-3 whitespace-pre-wrap break-words rounded-xl border ${borders.default} ${surfaces.secondary} ${textColors.secondary} p-3 text-xs leading-relaxed max-h-72 overflow-y-auto`}
              >
                {metadataJson}
              </pre>
            ) : null}
          </section>
        </div>
      )}
    </Modal>
  );
};

MessageDetailModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  messageId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onClose: PropTypes.func.isRequired,
  /** Called with the full message when the admin asks to cancel it (page shows the confirmation). */
  onCancel: PropTypes.func,
  cancelPending: PropTypes.bool,
  /** Called with the automation ref when the admin clicks the automation name. */
  onAutomationSelect: PropTypes.func,
};

export default MessageDetailModal;
